// ─────────────────────────────────────────────────────────────────────────────
// DEPTH ANYTHING V2 — monocular depth via ONNX Runtime Web
//
// Model: depth-anything-v2-small (25M params, ~50MB ONNX)
// Runs in browser WebAssembly (WASM) or WebGL backend
// Output: per-pixel relative depth map (0=far, 1=near)
//
// Used for: precise garment z-ordering behind arms and body parts
// Throttled: runs every 6 frames (~5fps) since depth changes slowly
//
// Model source: https://huggingface.co/onnx-community/depth-anything-v2-small
// ─────────────────────────────────────────────────────────────────────────────
import * as ort from "onnxruntime-web";

// Use the small model — best size/quality tradeoff for browser
const MODEL_URL =
  "https://huggingface.co/onnx-community/depth-anything-v2-small/resolve/main/onnx/model_fp16.onnx";

// Input size the model expects
const MODEL_W = 518;
const MODEL_H = 518;

export interface DepthMap {
  data:   Float32Array;  // normalized 0–1 per pixel (1 = closest)
  width:  number;
  height: number;
}

let _session:    ort.InferenceSession | null = null;
let _loading  = false;
let _failed   = false;
let _frameN   = 0;
const THROTTLE = 6;  // run every N frames

// Reuse these buffers across frames to avoid GC pressure
let _resizeCanvas: HTMLCanvasElement | null = null;
let _resizeCtx:    CanvasRenderingContext2D | null = null;

export async function loadDepthModel(): Promise<boolean> {
  if (_session) return true;
  if (_failed)  return false;
  if (_loading) {
    await new Promise<void>(res => {
      const iv = setInterval(() => { if (_session || _failed) { clearInterval(iv); res(); } }, 100);
    });
    return !!_session;
  }

  _loading = true;
  console.log("[DepthModel] Loading Depth Anything V2 Small...");

  try {
    // Prefer WebGL execution provider for GPU acceleration
    ort.env.wasm.numThreads = navigator.hardwareConcurrency ?? 4;
    ort.env.wasm.simd       = true;

    _session = await ort.InferenceSession.create(MODEL_URL, {
      executionProviders: ["webgl", "wasm"],
      graphOptimizationLevel: "all",
    });

    console.log("[DepthModel] ✓ Depth Anything V2 Small ready");
    return true;
  } catch (err) {
    console.warn("[DepthModel] Failed to load — depth estimation will use landmark Z", err);
    _failed = true;
    return false;
  } finally {
    _loading = false;
  }
}

export function isDepthModelReady(): boolean {
  return !!_session;
}

// ── Preprocess video frame → model input tensor ───────────────────────────────
function preprocessFrame(video: HTMLVideoElement): Float32Array {
  if (!_resizeCanvas) {
    _resizeCanvas = document.createElement("canvas");
    _resizeCanvas.width  = MODEL_W;
    _resizeCanvas.height = MODEL_H;
    _resizeCtx = _resizeCanvas.getContext("2d")!;
  }

  _resizeCtx!.drawImage(video, 0, 0, MODEL_W, MODEL_H);
  const pixels = _resizeCtx!.getImageData(0, 0, MODEL_W, MODEL_H).data;

  // ImageNet normalization (mean/std as used by Depth Anything V2)
  const mean = [0.485, 0.456, 0.406];
  const std  = [0.229, 0.224, 0.225];

  // CHW layout: [1, 3, H, W]
  const input = new Float32Array(3 * MODEL_H * MODEL_W);
  const total = MODEL_W * MODEL_H;

  for (let i = 0; i < total; i++) {
    input[i]             = (pixels[i * 4]     / 255 - mean[0]) / std[0]; // R
    input[i + total]     = (pixels[i * 4 + 1] / 255 - mean[1]) / std[1]; // G
    input[i + total * 2] = (pixels[i * 4 + 2] / 255 - mean[2]) / std[2]; // B
  }

  return input;
}

// ── Run depth inference ───────────────────────────────────────────────────────
export async function estimateDepth(
  video: HTMLVideoElement
): Promise<DepthMap | null> {
  if (!_session) return null;

  // Throttle: only run every N frames
  _frameN++;
  if (_frameN % THROTTLE !== 0) return null;

  try {
    const inputData = preprocessFrame(video);
    const inputTensor = new ort.Tensor("float32", inputData, [1, 3, MODEL_H, MODEL_W]);

    const feeds: Record<string, ort.Tensor> = {};
    // Input name varies by export — try common names
    const inputName = _session.inputNames[0];
    feeds[inputName] = inputTensor;

    const results = await _session.run(feeds);
    const outputName = _session.outputNames[0];
    const raw = results[outputName].data as Float32Array;

    // Normalize to 0–1 range and invert (model gives far=high, we want near=1)
    let min = Infinity, max = -Infinity;
    for (let i = 0; i < raw.length; i++) {
      if (raw[i] < min) min = raw[i];
      if (raw[i] > max) max = raw[i];
    }
    const range = max - min || 1;

    const normalized = new Float32Array(raw.length);
    for (let i = 0; i < raw.length; i++) {
      normalized[i] = 1 - (raw[i] - min) / range; // invert: near=1
    }

    return { data: normalized, width: MODEL_W, height: MODEL_H };
  } catch (err) {
    console.warn("[DepthModel] Inference error:", err);
    return null;
  }
}

// ── Sample depth at a normalized position (0–1 coords) ───────────────────────
export function sampleDepth(map: DepthMap, nx: number, ny: number): number {
  const x = Math.min(map.width  - 1, Math.round(nx * map.width));
  const y = Math.min(map.height - 1, Math.round(ny * map.height));
  return map.data[y * map.width + x];
}

// ── Determine if a body landmark is in FRONT of the garment plane ─────────────
// garmentDepth: 0–1 depth of the garment (estimated from shoulder Z)
export function landmarkInFront(
  map: DepthMap,
  lmX: number, lmY: number,
  garmentDepth: number,
  threshold = 0.05
): boolean {
  const d = sampleDepth(map, lmX, lmY);
  return d > garmentDepth + threshold; // landmark closer to camera than garment
}

// ── Build a garment occlusion mask using depth map ───────────────────────────
// Returns a Float32Array mask: 1 where body is IN FRONT of garment, 0 elsewhere
export function buildDepthOcclusionMask(
  depthMap: DepthMap,
  personMask: Float32Array,   // from segmenter (1=person pixel)
  garmentDepth: number,       // estimated garment z-depth (0–1, near=1)
  outputW: number,
  outputH: number
): Float32Array {
  const mask   = new Float32Array(outputW * outputH);
  const scaleX = depthMap.width  / outputW;
  const scaleY = depthMap.height / outputH;

  for (let y = 0; y < outputH; y++) {
    for (let x = 0; x < outputW; x++) {
      const idx   = y * outputW + x;
      if (!personMask[idx]) continue; // not a person pixel

      const dx    = Math.min(depthMap.width  - 1, Math.round(x * scaleX));
      const dy    = Math.min(depthMap.height - 1, Math.round(y * scaleY));
      const depth = depthMap.data[dy * depthMap.width + dx];

      // Pixel is body-in-front-of-garment if depth > garmentDepth
      mask[idx] = depth > garmentDepth + 0.04 ? 1 : 0;
    }
  }

  return mask;
}
